import * as React from 'react'
import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonContent,
  IonCard,
  IonCardContent,
  IonModal,
  IonSelectOption,
  IonSelect,
  IonItem,
  IonLabel,
  IonCheckbox,
} from '@ionic/react'
import { z } from 'zod'
import { useImmer } from 'use-immer'

import { getFullName } from '../../utils/format-helpers'
import {
  useAdminGroups,
  useAssociates,
} from '../../hooks/queries/use-associate'
import { createAutomationSchema } from '../automations-form.schema'
import {
  AccordionItem,
  AccordionContent,
  AccordionGroup,
  Accordion,
} from './conditions-accordion'
import { Input } from './conditions-input'

type ConditionsInput = z.input<typeof createAutomationSchema>['conditions']
type Props = {
  conditions: ConditionsInput
  onSearch: (conditions: ConditionsInput) => void
}

const initialFormState = {}

function ConditionsModal({ conditions, onSearch }: Props) {
  const modalRef = React.useRef<HTMLIonModalElement>(null)

  const [formState, updateFormState] = useImmer<ConditionsInput>(conditions)

  const associatesQuery = useAssociates()
  const flatAssociates = associatesQuery.data?.pages.flatMap((p) => p.data)
  const groupsQuery = useAdminGroups()

  function cancel() {
    updateFormState(conditions)
    modalRef.current?.dismiss()
  }

  function addConditions() {
    onSearch(formState)
    modalRef.current?.dismiss()
  }

  function resetForm() {
    updateFormState(initialFormState)
  }

  return (
    <>
      <IonButton id="open-modal" type="button" color="secondary">
        {Object.keys(conditions).length ? 'Edit' : 'Add'} Conditions
      </IonButton>
      <IonModal trigger="open-modal" ref={modalRef}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton color="secondary" onClick={cancel}>
                Cancel
              </IonButton>
            </IonButtons>
            <IonTitle>Conditions</IonTitle>
            <IonButtons slot="end">
              <IonButton fill="outline" color="secondary" onClick={resetForm}>
                Reset
              </IonButton>
              <IonButton color="yellow" fill="solid" onClick={addConditions}>
                {Object.keys(conditions).length ? 'Update' : 'Add'} Conditions
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding-bottom">
          <div className="ion-padding h-full overflow-y-scroll pr-2 scrollbar:h-1.5 scrollbar:w-1.5 scrollbar:bg-transparent scrollbar-track:rounded scrollbar-track:bg-[#1a1a1a] scrollbar-thumb:rounded scrollbar-thumb:bg-slate-500">
            <IonCard className="m-0">
              <IonCardContent className="space-y-4">
                <IonItem color="secondary">
                  <IonLabel>Assigned To</IonLabel>
                  <IonSelect
                    value={formState.assignedTo}
                    onIonChange={(e) => {
                      // BUG: IONCHANGE called even when remotely, don't change when resetting
                      if (!e.detail.value) return

                      updateFormState((draft) => {
                        draft.assignedTo = e.detail.value
                      })
                    }}
                    color="secondary"
                  >
                    {flatAssociates?.map(
                      ({ objectId, firstName, lastName }) => (
                        <IonSelectOption key={objectId} value={objectId}>
                          {getFullName({ firstName, lastName })}
                        </IonSelectOption>
                      )
                    )}
                  </IonSelect>
                </IonItem>

                {/* SoldBy */}
                <IonItem color="secondary">
                  <IonLabel>Sold By</IonLabel>
                  <IonSelect
                    value={formState.soldBy}
                    onIonChange={(e) => {
                      if (!e.detail.value) return
                      updateFormState((draft) => {
                        draft.soldBy = e.detail.value
                      })
                    }}
                    color="secondary"
                  >
                    {flatAssociates?.map(
                      ({ objectId, firstName, lastName }) => (
                        <IonSelectOption key={objectId} value={objectId}>
                          {getFullName({ firstName, lastName })}
                        </IonSelectOption>
                      )
                    )}
                  </IonSelect>
                </IonItem>

                {/* NO MESSAGES IN */}
                <div className="pl-5">
                  <div className="border-ion-color-step-500 grid grid-cols-3 items-center border-b pb-2 pr-2">
                    <label
                      className="text-ion-color-primary col-span-2"
                      color="primary"
                    >
                      No Messages In
                    </label>
                    <div className="flex w-full items-end gap-x-2">
                      <Input
                        type="number"
                        step="1"
                        value={formState.noMessages ?? ''}
                        onChange={(value) => {
                          updateFormState((draft) => {
                            draft.noMessages = value.length
                              ? parseInt(value, 10)
                              : null
                          })
                        }}
                      />
                      <span className="text-ion-color-primary">Days</span>
                    </div>
                  </div>
                </div>

                {/* NOT FIRST SALE */}
                <IonItem color="secondary">
                  <IonLabel>Not First Sale</IonLabel>
                  <IonCheckbox
                    color="secondary"
                    checked={formState.notFirstSale}
                    onIonChange={(e) => {
                      updateFormState((draft) => {
                        draft.notFirstSale = e.detail.checked
                      })
                    }}
                  />
                </IonItem>

                {/* Shopped At  */}
                <IonItem color="secondary">
                  <IonLabel>Shopped at</IonLabel>
                  <IonSelect
                    color="secondary"
                    value={formState.saleGroup}
                    onIonChange={(e) => {
                      if (!e.detail.value) return
                      updateFormState((draft) => {
                        draft.saleGroup = e.detail.value
                      })
                    }}
                    multiple
                  >
                    {groupsQuery.data?.map((item) => (
                      <IonSelectOption
                        key={item.objectId}
                        value={item.objectId}
                      >
                        {item.name}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>

                {/* Store Location */}
                <IonItem color="secondary">
                  <IonLabel>Store Location</IonLabel>
                  <IonSelect
                    color="secondary"
                    value={formState.clientGroup}
                    onIonChange={(e) => {
                      if (!e.detail.value) return
                      updateFormState((draft) => {
                        draft.clientGroup = e.detail.value
                      })
                    }}
                    multiple
                  >
                    {groupsQuery.data?.map((item) => (
                      <IonSelectOption
                        key={item.objectId}
                        value={item.objectId}
                      >
                        {item.name}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonItem>

                {/* LAST SALE */}
                <AccordionGroup>
                  <Accordion value="lastSale">
                    <AccordionItem label="Last Sale" />
                    <AccordionContent>
                      <div className="grid grid-cols-3 items-center py-0.5">
                        <label className="col-span-2" color="primary">
                          Greater Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.sale?.gt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.sale = { ...draft.sale, gt: value }
                            })
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Less Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.sale?.lt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.sale = { ...draft.sale, lt: value }
                            })
                          }}
                        />
                      </div>
                    </AccordionContent>
                  </Accordion>
                </AccordionGroup>

                {/* NO SALE IN */}
                <div className="pl-5">
                  <div className="border-ion-color-step-500 grid grid-cols-3 items-center border-b pb-2 pr-2">
                    <label
                      className="text-ion-color-primary col-span-2"
                      color="primary"
                    >
                      No Sale In
                    </label>
                    <div className="flex w-full items-end gap-x-2">
                      <Input
                        type="number"
                        step="1"
                        value={formState.noSales ?? ''}
                        onChange={(value) => {
                          updateFormState((draft) => {
                            draft.noSales = value.length
                              ? parseInt(value, 10)
                              : null
                          })
                        }}
                      />
                      <span className="text-ion-color-primary">Days</span>
                    </div>
                  </div>
                </div>

                {/* ACTIVE LOYALTY POINTS */}
                <AccordionGroup>
                  <Accordion value="activeLoyaltyPoints">
                    <AccordionItem label="Active Loyalty Points" />
                    <AccordionContent>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Greater Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.loyaltyPointsActive?.gt ?? ''}
                          onChange={(value) =>
                            updateFormState((draft) => {
                              draft.loyaltyPointsActive = {
                                ...draft.loyaltyPointsActive,
                                gt: value,
                              }
                            })
                          }
                        />
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Less Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.loyaltyPointsActive?.lt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.loyaltyPointsActive = {
                                ...draft.loyaltyPointsActive,
                                lt: value,
                              }
                            })
                          }}
                        />
                      </div>
                    </AccordionContent>
                  </Accordion>
                </AccordionGroup>

                {/* LIFETIME LOYALTY POINTS */}
                <AccordionGroup>
                  <Accordion value="lifetimeLoyaltyPoints">
                    <AccordionItem label="Lifetime Loyalty Points" />
                    <AccordionContent>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Greater Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.loyaltyPointsLifetime?.gt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.loyaltyPointsLifetime = {
                                ...draft.loyaltyPointsLifetime,
                                gt: value,
                              }
                            })
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Less Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.loyaltyPointsLifetime?.lt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.loyaltyPointsLifetime = {
                                ...draft.loyaltyPointsLifetime,
                                lt: value,
                              }
                            })
                          }}
                        />
                      </div>
                    </AccordionContent>
                  </Accordion>
                </AccordionGroup>

                {/* ACTIVE LOYALTY VISITS */}
                <AccordionGroup>
                  <Accordion value="activeLoyaltyVisits">
                    <AccordionItem label="Active Loyalty Visits" />
                    <AccordionContent>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Greater Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.loyaltyVisitsActive?.gt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.loyaltyVisitsActive = {
                                ...draft.loyaltyVisitsActive,
                                gt: value,
                              }
                            })
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-3 items-center">
                        <label className="col-span-2" color="primary">
                          Less Than
                        </label>
                        <Input
                          type="number"
                          step="1"
                          value={formState.loyaltyVisitsActive?.lt ?? ''}
                          onChange={(value) => {
                            updateFormState((draft) => {
                              draft.loyaltyVisitsActive = {
                                ...draft.loyaltyVisitsActive,
                                lt: value,
                              }
                            })
                          }}
                        />
                      </div>
                    </AccordionContent>
                  </Accordion>
                </AccordionGroup>
              </IonCardContent>
            </IonCard>
          </div>
        </IonContent>
      </IonModal>
    </>
  )
}

export { ConditionsModal }
